import * as Sentry from '@sentry/browser';

import React from 'react';
import PropTypes from 'prop-types';

import { getMenhirUrl } from '../../utils/config';

import Organization from '../../models/organization';

import EventRouter from './eventRouter';

import { ToastContext } from '@weezevent/nacre';

import useAppContext from '../../context/useAppContext';
import { usePermissions } from '../../user/permissions';
import { useTranslation } from 'react-i18next';
import useMixpanel from '../../context/useMixpanel';

import Loading from '../loading';
import { Switch, Route, Redirect } from 'react-router-dom';
import Template from '../template/template';
import Widgets from '../../pages/widgets/widgets';
import { EventSelection } from '../../pages/eventSelection';
import Exports from '../../pages/exports/exports';
import OrganizationStats from '../../pages/stats/organization-stats';
import RatesOrganization from '../../pages/rates/ratesOrganization';
import ChartsOrganization from '../../pages/charts/chartsOrganization';
import TagsOrganization from '../../pages/tags/tagsOrganization';
import PartnersOrganization from '../../pages/partner/partnersRouter';
import Mailing from '../../pages/mailing/mailRouter';
import ResalesRoute from '../../pages/resales/resalesRoute';
import { GoogleMapsContextProvider } from '../../context/googleMapsContext';
import EventCreationContainer from '../../pages/event_creation/eventCreationContainer';
import EventPostCreationContainer from '../../pages/event_creation/eventPostCreationContainer';
import Onboarding from '../../pages/onboarding/onboarding';
import ServicesOrganization from '../../pages/services/servicesOrganization';
import MyOrganization from '../../pages/organization/organization';
import TicketOfficesOrganization from '../../pages/ticket_offices/ticketOfficesOrganization';
import WeezBoRoute from '../migrations/weezRoute';
import TicketOfficeRouter from './ticketOfficeRouter';
import TicketsDesignRouter from './ticketsDesignRouter';
import WaitingListRouter from './waitingListRouter';
import PersonalSpacesList from '../../pages/widgets/personal_spaces/list/list';
import OnlineTicketingList from '../../pages/widgets/online_ticketing/list/list';
import PersonalSpaceRouter from './personalSpaceRouter';
import OnlineTicketingRouter from './onlineTicketingRouter';
import OnlineTicketingOrganization from '../../pages/widgets/online_ticketing/onlineTicketingOrganization';
import PersonalSpaceOrganization from '../../pages/personal_space/personalSpaceOrganization';
import ProductOrganization from '../../pages/product/productOrganization';
import { PrinterContextProvider } from '../../context/printerContext';
import WebserviceOrganization from '../../pages/webservicesV2/webservicesOrganization';
import WebserviceRouter from './webserviceRouter';
import PageError from '../../pages/error/pageError';
import AuthRouteOrganizer from '../../utils/authRouteOrganizer';
import PartnerDetailRouter from '../../pages/partner/partnerDetailRouter';
import ChartRouter from './chartRouter';
import TicketOfficesEmpty from '../../pages/ticket_office_empty';
import Api from '../../pages/api';
import MemberSpace from '../../pages/member_space';

import { ManagerContextProvider } from '../../pages/manager/components/context';
import ModerationContextProvider from '../../context/moderationContextProvider';
import ModerationRouter from './moderation/moderationRouter';
import ProductRouter from './productRouter';

const uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export default function OrganizationRouter({ organizationId, history, match }) {
    const permissions = usePermissions();
    const { t } = useTranslation();
    const [context, setContext] = useAppContext();
    const toast = React.useContext(ToastContext);
    const mixpanel = useMixpanel();
    const [orgaError, setOrgaError] = React.useState(null);

    const [, loadingOrganization, syncOrganization] = Organization.useApiModel(
        {
            id: organizationId,
            launch: Boolean(!context.organization || context.organization.id !== organizationId),
            cache: false,
            allow_cache: false,
            query: {
                sync: true,
                expand: 'business_entity'
            },
            onSuccess: response => {
                const organization = new Organization(response);
                setContext({ organization, syncOrganization, feature_flags: organization.feature_flags });

                // Set current orga
                Sentry.configureScope(scope => {
                    scope.setExtra('organization_id', organizationId);
                });

                mixpanel.register({
                    'Organizer ID': organizationId,
                    Application: 'WeezTicket',
                    'Event ID': null,
                    'Accounts ID': context?.connectData?.payload?.data?.user_id
                });
                // in case we don't have any info in settings prospection we redirect to onboarding for non-admins
                if ((!organization.terms_and_conditions || !organization.settings_prospection?.events_sector) && !permissions.isAdmin()) {
                    return history.push(`/ticket/O${organization.id}/onboarding`);
                }
                if (!organization.onboarding_date) {
                    history.push(`/ticket/O${organization.id}/events/new`);
                }
            },
            onError: e => {
                setOrgaError(e);
                console.error(e);
                toast.error(t('ticket.router.error.organizationRetrieval'));
            }
        },
        [organizationId]
    );

    const { path, url } = match;

    if (loadingOrganization) {
        return <Loading />;
    }

    if (permissions.roles?.length && !permissions.canAccessNBO(organizationId)) {
        const menhirUrl = getMenhirUrl();
        if (menhirUrl) {
            window.location.replace(`${menhirUrl}/bo/`);
        }
        return null;
    }

    // Redirect user to specific page if not Owner or Admin
    if (!context.organization?.id && orgaError) {
        return (
            <Template>
                <PageError errorCode={orgaError.code || null} />
            </Template>
        );
    }

    return (
        <PrinterContextProvider>
            <Switch>
                {/* Event selector redirection */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}
                    render={() => {
                        let redirect = `${url}/events`;
                        if (url[url.length - 1] === '/') {
                            redirect = `${url}events`;
                        }
                        return <Redirect to={redirect} />;
                    }}
                />
                <AuthRouteOrganizer exact path={`${path}/home`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                    <Redirect to={`${url}/events`} />
                </AuthRouteOrganizer>

                {/* eventsList */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/events`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}
                    render={({ match }) => (
                        <Template organization={context.organization}>
                            <ManagerContextProvider>
                                <EventSelection match={match} />
                            </ManagerContextProvider>
                        </Template>
                    )}
                />

                {/* eventCreation */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/events/new`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT_CREATE']}
                    render={() => (
                        <GoogleMapsContextProvider>
                            <EventCreationContainer organization={context.organization} />
                        </GoogleMapsContextProvider>
                    )}
                />

                {/* postCreationEvent */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/E:eventId/done`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}
                    render={({ match: { path, params }, history }) => <EventPostCreationContainer organization={context.organization} eventId={params.eventId} history={history} />}
                />

                {/* MyOrganisation */}
                <AuthRouteOrganizer
                    path={`${path}/organization`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION', 'PyvarCapacity.TRANSFERS', 'PyvarCapacity.BANK']}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <MyOrganization organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* CrossSellings */}
                <AuthRouteOrganizer
                    exact
                    allow={['PyvarCapacity.CROSS_SELLING']}
                    path={`${path}/products`}
                    render={props => (
                        <Template organization={context.organization}>
                            <ProductOrganization organization={context.organization} path={props.match.path} />
                        </Template>
                    )}
                />
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.CROSS_SELLING']}
                    path={`${path}/products/:productId`}
                    render={({ history, match }) => <ProductRouter match={match} history={history} organization={context.organization} productId={match.params.productId} />}
                />
                {getMenhirUrl() && <WeezBoRoute path={`${path}/transfers/redirect`} weezRoute={`${getMenhirUrl()}/bo/mon_compte_historique.php`} />}

                {/* tags */}
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.TAGS']}
                    path={`${path}/tags`}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <TagsOrganization organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* services */}
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SERVICE']}
                    path={`${path}/services`}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <ServicesOrganization organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* Api */}
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.API']}
                    path={`${path}/api`}
                    render={() => (
                        <Template organization={context.organization}>
                            <Api />
                        </Template>
                    )}
                />

                {/* Member space */}
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.MEMBER_SPACE']}
                    path={`${path}/member-space`}
                    render={() => (
                        <Template organization={context.organization}>
                            <MemberSpace />
                        </Template>
                    )}
                />

                {/* Ticket offices */}
                <AuthRouteOrganizer
                    path={`${path}/ticket_offices/:ticket_office_id`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.TICKET_OFFICE']}
                    render={({ match: { path } }) => <TicketOfficeRouter organization={context.organization} match={match} path={path} />}
                />
                <AuthRouteOrganizer
                    path={`${path}/ticket_offices`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.TICKET_OFFICE']}
                    fallback={
                        <Template organization={context.organization}>
                            <TicketOfficesEmpty />
                        </Template>
                    }
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <TicketOfficesOrganization organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* Tickets design */}
                <AuthRouteOrganizer
                    isAdmin={false}
                    path={`${path}/tickets_design`}
                    allow={['PyvarCapacity.DESIGN', 'PyvarCapacity.DESIGN_ADMIN', 'PyvarCapacity.DESIGN_TEMPLATES']}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <TicketsDesignRouter organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* rateTemplates */}
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.RATE_TEMPLATE']}
                    path={`${path}/rates`}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <RatesOrganization organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* statistics */}
                <AuthRouteOrganizer
                    path={`${path}/stats`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.STATS']}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <OrganizationStats organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />

                {/* charts */}
                <AuthRouteOrganizer
                    path={`${path}/charts/:chartUuid(${uuidRegex})`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.CHART']}
                    render={({ history, match }) => <ChartRouter match={match} history={history} organization={context.organization} chartUuid={match.params.chartUuid} />}
                />
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.CHART']}
                    path={`${path}/charts`}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <ChartsOrganization organization={context.organization} path={path} match={match} />
                        </Template>
                    )}
                />

                {/* webServices */}
                <AuthRouteOrganizer
                    path={`${path}/webservices/:subscriptionId([0-9]+)`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WEBSERVICE']}
                    render={({ match }) => <WebserviceRouter match={match} organization={context.organization} subscriptionId={match.params.subscriptionId} />}
                />
                <AuthRouteOrganizer
                    path={`${path}/webservices`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WEBSERVICE']}
                    render={({ history, match: { path } }) => (
                        <Template organization={context.organization}>
                            <WebserviceOrganization organization={context.organization} path={path} history={history} />
                        </Template>
                    )}
                />

                {/* resale */}
                <AuthRouteOrganizer
                    path={`${path}/resale`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.RESALE']}
                    render={props => (
                        <Template organization={context.organization}>
                            <ResalesRoute organizationId={organizationId} path={props.match.path} />
                        </Template>
                    )}
                />

                {/* Widgets*/}
                <AuthRouteOrganizer
                    path={`${path}/widgets`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET']}
                    render={({ match }) => (
                        <Template organization={context.organization}>
                            <Widgets organizationId={organizationId} path={match.path} />
                        </Template>
                    )}
                />

                {/* eventRouter */}
                {/* For some reason adding the (d+) blocks the regex*/}
                <AuthRouteOrganizer
                    path={`${path}/E:eventId`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}
                    render={({ match: { path, params, url } }) => <EventRouter eventId={parseInt(params.eventId)} path={path} url={url} />}
                />

                {/* reports */}
                <AuthRouteOrganizer
                    path={`${path}/statistics`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION']}
                    render={basenameProps => (
                        <Template organization={context.organization}>
                            <Switch>
                                <Route
                                    exact
                                    path={`${basenameProps.match.path}/reports`}
                                    render={() => <Exports organization={context.organization} path={basenameProps.match.path} />}
                                />
                                {/* Error */}
                                <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${context.organization.id}`} />} />
                            </Switch>
                        </Template>
                    )}
                />

                {/* partners */}
                <AuthRouteOrganizer
                    path={`${path}/partners/:partnerId(${uuidRegex})`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.PARTNER']}
                    render={({ match }) => <PartnerDetailRouter match={match} organization={context.organization} partnerId={match.params.partnerId} />}
                />
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.PARTNER']}
                    path={`${path}/partners`}
                    render={props => (
                        <Template organization={context.organization}>
                            <PartnersOrganization organization={context.organization} path={props.match.path} />
                        </Template>
                    )}
                />

                {/* personal space */}
                <AuthRouteOrganizer
                    path={`${path}/personal_spaces/:personalSpaceId`}
                    allow={['PyvarCapacity.PERSONALSPACE_ADMIN', 'PyvarCapacity.PERSONALSPACE']}
                    render={({ match }) => (
                        <PersonalSpaceRouter match={match} organization={context.organization} path={match.path} personalSpaceId={match.params.personalSpaceId} />
                    )}
                />
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.PERSONALSPACE_ADMIN', 'PyvarCapacity.PERSONALSPACE']}
                    path={`${path}/personal_spaces`}
                    render={props => (
                        <Template organization={context.organization}>
                            <PersonalSpaceOrganization organization={context.organization} path={props.match.path} />
                        </Template>
                    )}
                />
                {/* online ticketing */}
                <AuthRouteOrganizer
                    path={`${path}/online_ticketing/:onlineTicketingId`}
                    allow={['PyvarCapacity.MODULES_ADMIN', 'PyvarCapacity.MODULES']}
                    render={({ match }) => (
                        <OnlineTicketingRouter match={match} organization={context.organization} path={match.path} onlineTicketingId={match.params.onlineTicketingId} />
                    )}
                />
                <AuthRouteOrganizer
                    allow={['PyvarCapacity.MODULES_ADMIN', 'PyvarCapacity.MODULES']}
                    path={`${path}/online_ticketing`}
                    render={props => (
                        <Template organization={context.organization}>
                            <OnlineTicketingOrganization organization={context.organization} path={props.match.path} />
                        </Template>
                    )}
                />

                {/* mailing */}
                <AuthRouteOrganizer
                    path={`${path}/mailing`}
                    allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.MAIL']}
                    render={props => <Mailing organization={context.organization} path={props.match.path} />}
                />
                {/* Waiting list */}
                <AuthRouteOrganizer
                    isAdmin={false}
                    path={`${path}/waiting_lists`}
                    allow={['PyvarCapacity.WAITING_LIST_ADMIN', 'PyvarCapacity.WAITING_LIST']}
                    render={({ match: { path } }) => (
                        <Template organization={context.organization}>
                            <WaitingListRouter organization={context.organization} match={match} path={path} />
                        </Template>
                    )}
                />
                {/* Personal space */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/personal_spaces`}
                    allow={['PyvarCapacity.PERSONALSPACE_ADMIN', 'PyvarCapacity.PERSONALSPACE']}
                    render={() => (
                        <Template organization={context.organization}>
                            <ManagerContextProvider>
                                <PersonalSpacesList organization={context.organization} />
                            </ManagerContextProvider>
                        </Template>
                    )}
                />
                {/* Onine Ticketing */}
                <AuthRouteOrganizer
                    exact
                    path={`${path}/online_ticketing`}
                    allow={['PyvarCapacity.MODULES_ADMIN', 'PyvarCapacity.MODULES']}
                    render={() => (
                        <Template organization={context.organization}>
                            <ManagerContextProvider>
                                <OnlineTicketingList organization={context.organization} />
                            </ManagerContextProvider>
                        </Template>
                    )}
                />
                {/* Onboarding */}
                <AuthRouteOrganizer path={`${path}/onboarding`} allow={['PyvarCapacity.MARKETING']}>
                    <Onboarding organization={context.organization} />
                </AuthRouteOrganizer>

                {/*Moderation */}
                <AuthRouteOrganizer
                    path={`${path}/moderation`}
                    allow={['PyvarCapacity.MODERATION']}
                    render={({ match: { path } }) => (
                        <ModerationContextProvider>
                            <ModerationRouter path={path} />
                        </ModerationContextProvider>
                    )}
                />

                <Route
                    render={() => (
                        <Template organization={context.organization}>
                            <PageError errorCode={404} backHomeUrl={`/ticket/O${context.organization.id}`} />
                        </Template>
                    )}
                />
            </Switch>
        </PrinterContextProvider>
    );
}

OrganizationRouter.propTypes = {
    organizationId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};
